import { EditableDetails } from '../../../components/EditableDetails';
import { ChangeEvent, useState } from 'react';
import { useOperator } from '../../../contexts/OperatorContext';
import { isValidIpAddress, isValidPhoneNumber } from '../../../lib/validators';
import { ReviewItems } from './ReviewItems';
import { Switch } from '../../../components/Switch';
import { Input } from '../../../components/Input';
import { Label } from '../../../components/Label';

export const AccountDetails = (props: CreateAccountDetailsProps) => {
  const {
    asReview = false,
    onChange,
    optionMenus,
    formValues,
    updateFormState,
  } = props;
  const [isSingleIp, setIsSingleIp] = useState(
    formValues.startIpAddress === undefined
  );
  const classes = useStyles();
  const operator = useOperator();

  const operatorSystems = operator.systems
    .filter((s) => s.active)
    .map((s) => s.name);

  const generalA: DetailOption[] = [
    {
      display: true,
      label: 'Account Number',
      order: 0,
      required: true,
      type: 'input',
      input: (
        <Input
          name="accountNumber"
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange(event.target.value, 'accountNumber')
          }
          value={formValues.accountNumber}
        />
      ),
      value: formValues.accountNumber,
    },
    {
      display: true,
      label: 'Credit Limit',
      name: 'creditLimit',
      order: 1,
      type: 'select',
      menuItems: optionMenus.creditLimit,
      onChange: (value) => onChange(value, 'creditLimit'),
      value: asReview
        ? `$${formValues.creditLimit ?? 0}`
        : formValues.creditLimit ?? 0,
    },
  ];

  const generalB: DetailOption[] = [
    {
      display: true,
      label: 'Name',
      name: 'enhancedData',
      order: 0,
      type: 'input',
      input: (
        <Input
          name="enhancedData"
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange(event.target.value, 'enhancedData')
          }
          value={formValues.enhancedData}
        />
      ),
      value: formValues.enhancedData ?? '',
    },
    {
      display: true,
      label: 'Phone',
      name: 'phoneNumber',
      order: 1,
      type: 'input',
      input: (
        <Input
          name="phoneNumber"
          error={!isValidPhoneNumber(formValues.phoneNumber ?? '')}
          inputProps={{ maxLength: 10 }}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange(event.target.value, 'phoneNumber')
          }
          value={formValues.phoneNumber}
        />
      ),
      value: formValues.phoneNumber ?? '',
    },
  ];

  const generalC: DetailOption[] = [
    {
      display: true,
      label: 'County',
      name: 'fipsCode',
      order: 0,
      required: true,
      type: 'select',
      menuItems: optionMenus.fipsCode,
      onChange: (value) => onChange(value, 'fipsCode'),
      value: asReview
        ? optionMenus.fipsCode.find((t) => t.id === formValues.fipsCode)
            ?.name ?? ''
        : formValues.fipsCode,
    },
    {
      display: true,
      label: 'Transport',
      name: 'transportTypeName',
      order: 1,
      required: true,
      type: 'select',
      menuItems: optionMenus.transportTypeName,
      onChange: (value) => onChange(value, 'transportTypeName'),
      value: asReview
        ? optionMenus.transportTypeName.find(
            (t) => t.id === formValues.transportTypeName
          )?.name ?? ''
        : formValues.transportTypeName,
    },
    {
      display: true,
      label: 'Timezone',
      name: 'timezoneName',
      order: 2,
      type: 'select',
      menuItems: operator.availableTimeZones?.map((t) => ({
        id: t,
        name: t,
      })) ?? [
        {
          id: operator.timeZone ?? 'N/A',
          name: operator.timeZone ?? 'N/A',
        },
      ],
      onChange: (value) => onChange(value, 'timezoneName'),
      value: formValues.timezoneName,
    },
  ];

  const mediafirst: DetailOption[] = [
    {
      display: true,
      label: 'Experience Group',
      name: 'experienceGroup',
      order: 0,
      type: 'select',
      menuItems: optionMenus.experienceGroup,
      onChange: (value) => onChange(value, 'experienceGroup'),
      value: asReview
        ? optionMenus.experienceGroup.find(
            (t) => t.id === formValues.experienceGroup
          )?.name ?? ''
        : formValues.experienceGroup ?? '',
    },
    {
      display: true,
      label: 'Routing Group',
      name: 'routingGroup',
      order: 1,
      type: 'select',
      menuItems: optionMenus.routingGroup,
      onChange: (value) => onChange(value, 'routingGroup'),
      value: asReview
        ? optionMenus.routingGroup.find((t) => t.id === formValues.routingGroup)
            ?.name ?? ''
        : formValues.routingGroup ?? '',
    },
  ];

  const mediafirstAccess: DetailOption[] = [
    {
      order: 0,
      display: !isSingleIp,
      input: (
        <Input
          name="startIpAddress"
          className="w-[15ch]"
          error={!isValidIpAddress(formValues.startIpAddress ?? '')}
          inputProps={{ maxLength: 15 }}
          value={formValues.startIpAddress}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange(event.target.value, 'startIpAddress')
          }
        />
      ),
      label: 'Start IP',
      required: !isSingleIp,
      type: 'input',
      value: formValues.startIpAddress ?? '',
    },
    {
      order: 1,
      display: true,
      input: (
        <Input
          name="ipAddress"
          className="w-[15ch]"
          error={!isValidIpAddress(formValues.ipAddress ?? '')}
          inputProps={{ maxLength: 15 }}
          value={formValues.ipAddress}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange(event.target.value, 'ipAddress')
          }
        />
      ),
      label: isSingleIp ? 'IP Address' : 'End IP',
      required: !isSingleIp,
      type: 'input',
      value: formValues.ipAddress ?? '',
    },
    {
      display: !operator.usesTgOAuth,
      label: 'Login ID',
      order: 2,
      type: 'input',
      input: (
        <Input
          className="w-[300px]"
          name="loginId"
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange(event.target.value, 'loginId')
          }
          value={formValues.loginId}
        />
      ),
      value: formValues.loginId ?? '',
    },
  ];

  const statusOptions: DetailOption[] = [
    {
      display: operator.mediaroom ?? false,
      label: 'Mediaroom',
      order: 0,
      type: 'input',
      input: (
        <Switch
          name="disableMediaroom"
          color="primary"
          checked={!formValues.disableMediaroom}
          onChange={() =>
            updateFormState({
              disableMediaroom: !formValues.disableMediaroom,
            })
          }
        />
      ),
      value: formValues.disableMediaroom ?? false,
    },
    {
      display: operator.mediafirst ?? false,
      label: 'MediaFirst',
      order: 1,
      type: 'input',
      input: (
        <Switch
          name="disableMediafirst"
          color="primary"
          checked={!formValues.disableMediafirst}
          onChange={() =>
            updateFormState({
              disableMediafirst: !formValues.disableMediafirst,
            })
          }
        />
      ),
      value: formValues.disableMediafirst ?? false,
    },
  ];

  return (
    <div className="flex flex-col p-4">
      <section className={classes.section}>
        {asReview ? (
          <>
            <ReviewItems
              items={generalA.map((item) => ({
                defaultValue: 'N/A',
                label: item.label,
                required: item.required ?? false,
                value: item.value,
              }))}
            />
            <ReviewItems
              items={generalB.map((item) => ({
                defaultValue: 'N/A',
                label: item.label,
                required: item.required ?? false,
                value: item.value,
              }))}
            />
            <ReviewItems
              items={generalC.map((item) => ({
                defaultValue: 'N/A',
                label: item.label,
                required: item.required ?? false,
                value: item.value,
              }))}
            />
          </>
        ) : (
          <>
            <EditableDetails
              editing={true}
              loading={false}
              options={generalA}
            />
            <EditableDetails
              editing={true}
              loading={false}
              options={generalB}
            />
            <EditableDetails
              editing={true}
              loading={false}
              options={generalC}
            />
          </>
        )}
      </section>
      {operatorSystems.includes('Residential MediaFirst') ||
      operatorSystems.includes('MediaFirst') ? (
        <>
          <p>MediaFirst</p>
          <section className={classes.section}>
            {asReview ? (
              <>
                <ReviewItems
                  items={mediafirst
                    .filter((item) => item.display)
                    .map((item) => ({
                      defaultValue: 'N/A',
                      label: item.label,
                      required: item.required ?? false,
                      value: item.value,
                    }))}
                />
                <ReviewItems
                  items={mediafirstAccess
                    .filter((item) => item.display)
                    .map((item) => ({
                      defaultValue: 'N/A',
                      label: item.label,
                      required: item.required ?? false,
                      value: item.value,
                    }))}
                />
              </>
            ) : (
              <>
                <EditableDetails
                  editing={true}
                  loading={false}
                  options={mediafirst}
                />
                <div className="flex w-full">
                  <div className="flex flex-col items-start mr-4">
                    <Label text={isSingleIp ? 'Single IP' : 'IP Range'}>
                      <Switch
                        color="primary"
                        checked={!isSingleIp}
                        onChange={(event) => {
                          setIsSingleIp(!event.target.checked);
                          updateFormState({
                            startIpAddress: !event.target.checked
                              ? undefined
                              : '',
                          });
                        }}
                      />
                    </Label>
                  </div>
                  <EditableDetails
                    editing
                    loading={false}
                    options={mediafirstAccess}
                  />
                </div>
              </>
            )}
          </section>
        </>
      ) : null}
      <p>Account Status</p>
      <section className={classes.section}>
        {asReview ? (
          <ReviewItems
            items={statusOptions
              .filter((item) => item.display)
              .map((item) => ({
                defaultValue: 'N/A',
                label: item.label,
                required: item.required ?? false,
                value: item.value === false ? 'Enable' : 'Disable',
              }))}
          />
        ) : (
          <EditableDetails editing loading={false} options={statusOptions} />
        )}
      </section>
    </div>
  );
};
const useStyles = () => ({
  section: 'flex flex-col gap-2 p-4',
});
